import React, { useState, useEffect } from "react";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode } from "primereact/api";
import LoadingOverlay from "react-loading-overlay";
import { Triangle } from "react-loader-spinner";
import { InputText } from "primereact/inputtext";
import { handleGetRequest } from "../../services/GetTemplate";
import { useDispatch } from "react-redux";
import { Tag } from "primereact/tag";
import { Dialog } from "primereact/dialog";

const ManageOrder = () => {

    const dispatch = useDispatch()

    const [isActive, setIsActive] = useState(false)
    const [allOrders, setAllOrders] = useState([]);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [orderDetailDialog, setOrderDetailDialog] = useState(false);

    const [globalFilterValue, setGlobalFilterValue] = useState("");
    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    });

    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        let _filters = { ...filters };
        _filters["global"].value = value;

        setFilters(_filters);
        setGlobalFilterValue(value);
    };

    //Get Order List
    const getOrderList = async () => {
        setIsActive(true);
        const response = await dispatch(handleGetRequest(`/orders/getAll`, true));
        if (response?.status === 'success' || response?.statusCode === 200) {
            setAllOrders(response?.data || []);
        }
        setIsActive(false);
    };

    useEffect(() => {
        getOrderList();
    }, [])

    const dateTemplate = (rowData) => {
        if (rowData?.createdAt) {
            const date = new Date(rowData.createdAt);
            return date.toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        }
        return 'N/A';
    };

    const statusTemplate = (rowData) => {
        const status = rowData?.status || 'pending';
        const severityMap = {
            'pending': 'warning',
            'confirmed': 'info',
            'preparing': 'info',
            'out_for_delivery': 'primary',
            'delivered': 'success',
            'cancelled': 'danger'
        };
        const statusLabels = {
            'pending': 'Pending',
            'confirmed': 'Confirmed',
            'preparing': 'Preparing',
            'out_for_delivery': 'Out for Delivery',
            'delivered': 'Delivered',
            'cancelled': 'Cancelled'
        };
        return (
            <Tag 
                value={statusLabels[status] || status} 
                severity={severityMap[status] || 'secondary'} 
            />
        );
    };

    const paymentStatusTemplate = (rowData) => {
        const paymentStatus = rowData?.paymentStatus || 'pending';
        const severityMap = {
            'pending': 'warning',
            'paid': 'success',
            'failed': 'danger',
            'refunded': 'info'
        };
        const statusLabels = {
            'pending': 'Pending',
            'paid': 'Paid',
            'failed': 'Failed',
            'refunded': 'Refunded'
        };
        return (
            <Tag 
                value={statusLabels[paymentStatus] || paymentStatus} 
                severity={severityMap[paymentStatus] || 'secondary'} 
            />
        );
    };

    const totalAmountTemplate = (rowData) => {
        return `$${(rowData?.totalAmount || 0).toFixed(2)}`;
    };

    const customerTemplate = (rowData) => {
        return (
            <div>
                <div style={{ fontWeight: 'bold' }}>{rowData?.customerName || 'N/A'}</div>
                <div style={{ fontSize: '0.85em', color: '#666' }}>{rowData?.customerEmail || ''}</div>
                <div style={{ fontSize: '0.85em', color: '#666' }}>{rowData?.customerPhone || ''}</div>
            </div>
        );
    };

    const itemsTemplate = (rowData) => {
        const items = rowData?.items || [];
        return (
            <div>
                <span style={{ fontWeight: 'bold' }}>{items.length} item(s)</span>
                {/* <Button
                    label="View Items"
                    icon="pi pi-eye"
                    className="p-button-text p-button-sm ml-2"
                    onClick={() => {
                        setSelectedOrder(rowData);
                        setOrderDetailDialog(true);
                    }}
                /> */}
            </div>
        );
    };

    const actionTemplate = (rowData) => {
        return (
            <Button
                label="View Details"
                icon="pi pi-eye"
                className="p-button-sm"
                onClick={() => {
                    setSelectedOrder(rowData);
                    setOrderDetailDialog(true);
                }}
            />
        );
    };

    const orderDetailFooter = (
        <div>
            <Button
                label="Close"
                icon="pi pi-times"
                onClick={() => setOrderDetailDialog(false)}
                className="p-button-text"
            />
        </div>
    );

    return (
        <>
            <div className="flex flex-column align-items-start md:flex-row  md:justify-content-between md:align-items-center ">
                <h4 className="mb-0" style={{ fontWeight: '600', letterSpacing: '0.5px', color: 'black' }}>
                    Orders Management
                </h4>
                <div className="flex flex-column align-items-start  md:flex-row md:align-items-center">
                    <span className="p-input-icon-left mr-3 resp-margin my-3 md:my-0">
                        <i className="pi pi-search" />
                        <InputText
                            placeholder="Search"
                            value={globalFilterValue}
                            onChange={onGlobalFilterChange}
                            className="search-input"
                        />
                    </span>
                </div>
            </div>

            <div className="card mt-3">
                <LoadingOverlay
                    active={isActive}
                    spinner={<Triangle
                        height="120"
                        width="120"
                        color="#dc2626"
                        ariaLabel="triangle-loading"
                        wrapperStyle={{}}
                        wrapperClassName=""
                        visible={true}
                    />}
                    styles={{
                        overlay: (base) => ({
                            ...base,
                            background: 'rgb(38 41 51 / 78%)',
                        })
                    }}
                >
                    <DataTable
                        responsive={true}
                        filters={filters}
                        globalFilterFields={[
                            "customerName",
                            "customerEmail",
                            "customerPhone",
                            "deliveryAddress",
                            "status",
                            "paymentStatus",
                        ]}
                        responsiveLayout="scroll"
                        stripedRows
                        paginator
                        rows={20}
                        value={allOrders}
                        emptyMessage="No orders found"
                        paginatorClassName="custom-paginator"
                        paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
                        rowsPerPageOptions={[10, 20, 30, 40, 50]}
                    >
                        <Column field='customerName' body={customerTemplate} header="Customer"></Column>
                        <Column field='deliveryAddress' body={(rowData) => rowData?.deliveryAddress || 'N/A'} header="Delivery Address"></Column>
                        <Column field='items' body={itemsTemplate} header="Items"></Column>
                        <Column field='subtotal' body={(rowData) => `$${(rowData?.subtotal || 0).toFixed(2)}`} header="Subtotal"></Column>
                        <Column field='deliveryFee' body={(rowData) => `$${(rowData?.deliveryFee || 0).toFixed(2)}`} header="Delivery Fee"></Column>
                        {allOrders.some(order => order?.promocodeUsed) && (
                            <Column field='promocodeUsed' body={(rowData) => rowData?.promocodeUsed || '-'} header="Promocode"></Column>
                        )}
                        {allOrders.some(order => order?.discountAmount > 0) && (
                            <Column field='discountAmount' body={(rowData) => `-$${(rowData?.discountAmount || 0).toFixed(2)}`} header="Discount"></Column>
                        )}
                        <Column sortable field='totalAmount' body={totalAmountTemplate} header="Total Amount"></Column>
                        {/* <Column field='status' body={statusTemplate} header="Status"></Column> */}
                        {/* <Column field='paymentStatus' body={paymentStatusTemplate} header="Payment Status"></Column> */}
                        <Column field='createdAt' body={dateTemplate} header="Order Date"></Column>
                        <Column header="Action" body={actionTemplate} />
                    </DataTable>
                </LoadingOverlay>
            </div>

            {/* Order Detail Dialog */}
            <Dialog
                header="Order Details"
                visible={orderDetailDialog}
                style={{ width: '90vw', maxWidth: '800px' }}
                footer={orderDetailFooter}
                onHide={() => setOrderDetailDialog(false)}
                className="order-detail-dialog"
            >
                {selectedOrder && (
                    <div className="order-detail-content">
                        {/* Order Information Section */}
                        <div className="mb-4">
                            <h5 className="mb-3" style={{ color: '#dc2626', borderBottom: '2px solid #dc2626', paddingBottom: '8px' }}>
                                Order Information
                            </h5>
                            <div className="grid">
                                <div className="col-12 md:col-6">
                                    <div className="order-info-item mb-3">
                                        <label>Order Date:</label>
                                        <span>{new Date(selectedOrder.createdAt).toLocaleString()}</span>
                                    </div>
                                    {/* <div className="order-info-item mb-3">
                                        <label>Status:</label>
                                        <span>{statusTemplate(selectedOrder)}</span>
                                    </div>
                                    <div className="order-info-item mb-3">
                                        <label>Payment Status:</label>
                                        <span>{paymentStatusTemplate(selectedOrder)}</span>
                                    </div> */}
                                </div>
                                <div className="col-12 md:col-6">
                                    <div className="order-info-item mb-3">
                                        <label>Customer Name:</label>
                                        <span>{selectedOrder.customerName}</span>
                                    </div>
                                    <div className="order-info-item mb-3">
                                        <label>Email:</label>
                                        <span>{selectedOrder.customerEmail}</span>
                                    </div>
                                    <div className="order-info-item mb-3">
                                        <label>Phone:</label>
                                        <span>{selectedOrder.customerPhone}</span>
                                    </div>
                                    <div className="order-info-item mb-3">
                                        <label>Delivery Address:</label>
                                        <span>{selectedOrder.deliveryAddress}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Order Items Section */}
                        <div className="mb-4">
                            <h5 className="mb-3" style={{ color: '#dc2626', borderBottom: '2px solid #dc2626', paddingBottom: '8px' }}>
                                Order Items
                            </h5>
                            <DataTable
                                value={selectedOrder.items || []}
                                emptyMessage="No items"
                                responsiveLayout="scroll"
                                className="order-items-table"
                            >
                                <Column field="title" header="Item" style={{ minWidth: '200px' }} />
                                <Column field="quantity" header="Qty" style={{ width: '80px' }} />
                                <Column 
                                    field="price" 
                                    body={(rowData) => `$${(rowData?.price || 0).toFixed(2)}`} 
                                    header="Price" 
                                    style={{ width: '120px' }}
                                />
                                <Column 
                                    field="total" 
                                    body={(rowData) => `$${(rowData?.total || 0).toFixed(2)}`} 
                                    header="Total" 
                                    style={{ width: '120px' }}
                                />
                            </DataTable>
                        </div>

                        {/* Order Summary Section */}
                        <div>
                            <h5 className="mb-3" style={{ color: '#dc2626', borderBottom: '2px solid #dc2626', paddingBottom: '8px' }}>
                                Order Summary
                            </h5>
                            <div className="flex justify-content-end">
                                <div style={{ width: '100%', maxWidth: '400px' }}>
                                    <div className="flex justify-content-between mb-2" style={{ padding: '8px 0' }}>
                                        <span style={{ fontSize: '14px' }}>Subtotal:</span>
                                        <span style={{ fontSize: '14px', fontWeight: '500' }}>${(selectedOrder.subtotal || 0).toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-content-between mb-2" style={{ padding: '8px 0' }}>
                                        <span style={{ fontSize: '14px' }}>Delivery Fee:</span>
                                        <span style={{ fontSize: '14px', fontWeight: '500' }}>${(selectedOrder.deliveryFee || 0).toFixed(2)}</span>
                                    </div>
                                    {selectedOrder.promocodeUsed && (
                                        <div className="flex justify-content-between mb-2" style={{ padding: '8px 0', color: '#dc2626' }}>
                                            <span style={{ fontSize: '14px' }}>Promocode ({selectedOrder.promocodeUsed}):</span>
                                            <span style={{ fontSize: '14px', fontWeight: '500' }}>-${(selectedOrder.discountAmount || 0).toFixed(2)}</span>
                                        </div>
                                    )}
                                    <div 
                                        className="flex justify-content-between mt-3 pt-3" 
                                        style={{ 
                                            borderTop: '2px solid #ddd',
                                            fontSize: '18px',
                                            fontWeight: 'bold',
                                            color: '#dc2626'
                                        }}
                                    >
                                        <span>Total Amount:</span>
                                        <span>${(selectedOrder.totalAmount || 0).toFixed(2)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </Dialog>

            <style>{`
                .order-detail-content {
                    padding: 10px 0;
                }
                .order-info-item {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                }
                .order-info-item label {
                    font-weight: 600;
                    color: #333;
                    font-size: 13px;
                    margin-bottom: 4px;
                }
                .order-info-item span {
                    color: #666;
                    font-size: 14px;
                }
                .order-items-table .p-datatable-thead > tr > th {
                    background-color: #f8f9fa;
                    font-weight: 600;
                }
                .order-detail-dialog .p-dialog-content {
                    padding: 1.5rem;
                }
            `}</style>
        </>
    );
};

export default ManageOrder;

